import { Spinner } from '@meshhub/design-tokens/ui';
import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Forbidden } from '../../components/Forbidden.web.jsx';
import { useSession } from '../../lib/session.jsx';
import { S } from '../../lib/strings.js';

/**
 * `adminOnly: false` means every authenticated user may open it.
 *
 * There is exactly one such section, and the exception is real rather than a
 * convenience: a Google connection is ONE PERSON'S OWN calendar. An admin
 * cannot connect one on somebody's behalf — that would mean holding their
 * credentials — so the page cannot be admin-gated without making the feature
 * unusable for the people it is for. The scheduling service enforces the same
 * thing from the other side by refusing any user id but the caller's.
 */
const SECTIONS = [
  { to: 'lookups', label: S.settings.sections.lookups, adminOnly: true },
  { to: 'specialties', label: S.settings.sections.specialties, adminOnly: true },
  { to: 'tags', label: S.settings.sections.tags, adminOnly: true },
  { to: 'custom-fields', label: S.settings.sections.customFields, adminOnly: true },
  { to: 'templates', label: S.settings.sections.templates, adminOnly: true },
  { to: 'activity-types', label: S.settings.sections.activityTypes, adminOnly: true },
  { to: 'checklists', label: S.checklists.templates, adminOnly: true },
  { to: 'issue-types', label: S.settings.sections.issueTypes, adminOnly: true },
  { to: 'integrations', label: S.integrations.title, adminOnly: false },
  { to: 'automation', label: S.automation.title, adminOnly: true },
  { to: 'whatsapp', label: S.whatsapp.title, adminOnly: true },
  { to: 'import', label: S.settings.sections.import, adminOnly: true },
  { to: 'users', label: S.settings.sections.users, adminOnly: true },
  // The ops panel. ADMIN, because it is a map of the deployment: which
  // service is down, how far behind the read models are, and who did what.
  { to: 'system', label: S.ops.title, adminOnly: true },
];

const SELF_SERVICE = SECTIONS.filter((section) => !section.adminOnly).map((s) => s.to);

/**
 * Guards the /settings tree on the server-verified role.
 *
 * Hiding a nav entry is a convenience, not a control: direct navigation lands
 * here, and this is what actually refuses. The BFF and the services refuse
 * independently, so a bypass of this screen still buys nothing.
 */
export function SettingsLayout() {
  const { isAdmin, loading } = useSession();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // A USER may reach the self-service sections and nothing else. Matched on the
  // path segment rather than a prefix, so `/settings/integrations-secret` is
  // not accidentally allowed by a `startsWith`.
  const section = location.pathname.split('/').filter(Boolean)[1];
  if (!isAdmin && !SELF_SERVICE.includes(section)) return <Forbidden />;

  const visible = SECTIONS.filter((entry) => isAdmin || !entry.adminOnly);

  return (
    <div className="flex h-screen">
      <nav className="flex w-sidebar shrink-0 flex-col gap-1 border-e border-border bg-surface p-3">
        <span className="px-4 py-2 text-sm text-text-muted">{S.settings.subtitle}</span>
        {visible.map((entry) => (
          <NavLink
            key={entry.to}
            to={entry.to}
            className={({ isActive }) =>
              [
                'flex h-control items-center rounded px-4 text-base transition-colors',
                isActive ? 'bg-primary text-primary-contrast' : 'text-text hover:bg-surface-hover',
              ].join(' ')
            }
          >
            {entry.label}
          </NavLink>
        ))}
      </nav>

      <div className="min-w-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}
